import React from "react";
import { Link } from "react-router-dom";
import FireLogo from "../components/FireLogo";
import Footer from "../components/Footer";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div
          className="absolute -top-24 left-10 h-60 w-60 rounded-full opacity-40 blur-3xl animate-float"
          style={{ backgroundColor: "var(--accent-3)" }}
        />
        <div
          className="absolute top-10 right-10 h-72 w-72 rounded-full opacity-25 blur-3xl animate-float-slow"
          style={{ backgroundColor: "var(--accent-2)" }}
        />
        <div className="hero-container relative mx-auto max-w-6xl px-4 pb-12 pt-12">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="inline-flex items-center gap-3 rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
              <FireLogo size={40} data-hero-logo />
              <span>BetterBMRCalculator</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-xs font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
              >
                Back to calculator
              </Link>
              <Link
                to="/donate"
                className="inline-flex items-center justify-center rounded-full bg-[var(--accent)] px-5 py-2 text-xs font-semibold text-white shadow-[0_18px_40px_rgba(227,108,79,0.35)] transition hover:-translate-y-0.5"
              >
                Support the project
              </Link>
            </div>
          </div>
          <div className="mt-6 max-w-3xl space-y-3">
            <h1 className="text-3xl font-display leading-tight text-[var(--ink)] md:text-4xl">
              Privacy policy
            </h1>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
              Last updated Feb 5, 2026
            </p>
            <p className="text-base text-[var(--muted)]">
              This policy explains what data we collect, why we collect it,
              and how you can control it.
            </p>
          </div>
        </div>
      </div>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            Summary
          </h2>
          <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)] md:grid-cols-2">
            <li>Calculator inputs stay on your device.</li>
            <li>Donations use Stripe. We never see your card details.</li>
            <li>Donation requests are public after approval.</li>
            <li>No ads, no selling data.</li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            What we collect
          </h2>
          <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)]">
            <li>
              Calculator inputs are saved locally in your browser so you
              can return later without retyping.
            </li>
            <li>
              If you donate, we store your display name, donation amount,
              and any request text you submit.
            </li>
            <li>
              If enabled for fraud prevention, we may store donor email,
              IP address, and user agent. These are optional and can be
              disabled by configuration.
            </li>
            <li>
              Authentication is provided by Clerk. We do not store your
              passwords or OAuth credentials.
            </li>
            <li>
              Bot protection uses Cloudflare Turnstile, which may collect
              device and usage signals to verify you are human.
            </li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            How we use data
          </h2>
          <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)]">
            <li>To calculate BMR/TDEE on the device.</li>
            <li>To show public donation requests and totals.</li>
            <li>To prevent abuse and keep the donation system safe.</li>
            <li>To operate the site (security, performance, reliability).</li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            Third-party services
          </h2>
          <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)]">
            <li>Stripe handles payment processing.</li>
            <li>Clerk handles authentication and user sessions.</li>
            <li>Cloudflare Turnstile handles anti-bot checks.</li>
            <li>The site is hosted on Vercel and Render.</li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            Your choices
          </h2>
          <ul className="mt-4 grid gap-3 text-sm text-[var(--muted)]">
            <li>You can clear your calculator data by deleting site data in your browser.</li>
            <li>
              If you want a donation request removed, contact us and we
              will help.
            </li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 pb-16">
        <div className="glass-card p-6 md:p-8">
          <h2 className="text-2xl font-display text-[var(--ink)]">
            Contact
          </h2>
          <p className="mt-3 text-sm text-[var(--muted)]">
            For privacy questions or data requests, contact Maxwell
            McInnis via LinkedIn.
          </p>
          <div className="mt-4">
            <a
              href="https://www.linkedin.com/in/maxwellmcinnis"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center rounded-full border border-white/70 bg-white/80 px-5 py-2 text-sm font-semibold text-[var(--ink)] transition hover:-translate-y-0.5 hover:shadow-lg"
            >
              Contact on LinkedIn
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
